import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { OrgChartEmployee } from '../interfaces';
import { SELECTED_EMPLOYEE_QUERY_PARAM } from './constants';
import { OrgChartContextProps, OrgChartProviderProps } from './interfaces';

export type OrgChartPath = string[]; // Array of employee IDs representing a path

const OrgChartContext = createContext<OrgChartContextProps | undefined>(undefined);

const OrgChartProvider: React.FC<OrgChartProviderProps> = ({ children, employees }) => {
  const [employeeList] = useState<OrgChartEmployee[]>(employees);
  const [selectedEmployee, setSelectedEmployee] = useState<string | null>(null);
  const [expandedPath, setExpandedPath] = useState<OrgChartPath>([]);
  const [expandedEmployees, setExpandedEmployees] = useState<Set<string>>(new Set());

  const findParentEmployee = useCallback(
    (employeeId: string): string | null => {
      const employee = employeeList.find((emp) => emp.reports && emp.reports.includes(employeeId));
      return employee ? employee.id : null;
    },
    [employeeList]
  );

  // Function to find all possible paths to a specific employee
  const findAllPaths = useCallback(
    (employeeId: string) => {
      const results: { path: OrgChartPath; employee: OrgChartEmployee }[] = [];
      const employee = employeeList.find((e) => e.id === employeeId);

      if (!employee) return results;

      // find paths recursively!!
      const findPathsRecursive = (
        currentEmployeeId: string,
        currentPath: OrgChartPath,
        visited: Set<string>
      ) => {
        // Base case: if we've already visited this employee, stop
        if (visited.has(currentEmployeeId)) return;

        const newVisited = new Set(visited);
        newVisited.add(currentEmployeeId);

        const currentEmployee = employeeList.find((e) => e.id === currentEmployeeId);
        if (!currentEmployee) return;

        const newPath = [...currentPath, currentEmployeeId];

        // If this is the target employee, add the path to results
        if (currentEmployeeId === employeeId) {
          results.push({
            path: newPath,
            employee: currentEmployee,
          });
          return;
        }

        // Check reports
        if (currentEmployee.reports) {
          for (const reportId of currentEmployee.reports) {
            findPathsRecursive(reportId, newPath, newVisited);
          }
        }
      };

      // Find root employees (those with no manager)
      const rootEmployees = employeeList.filter((e) => e.reportsToId === null);

      // Start search from each root employee
      for (const rootEmployee of rootEmployees) {
        findPathsRecursive(rootEmployee.id, [], new Set());
      }
      return results;
    },
    [employeeList]
  );

  // Select an employee (just updates selection, doesn't change expansion)
  const selectEmployee = useCallback((employeeId: string) => {
    setSelectedEmployee(employeeId);

    // Update URL query parameter
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.set('selected_employee', employeeId);
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  // Check if an employee is expanded
  const isEmployeeExpanded = useCallback(
    (employeeId: string) => expandedEmployees.has(employeeId),
    [expandedEmployees]
  );

  // Toggle expansion state for an employee
  const toggleEmployeeExpansion = useCallback(
    (employeeId: string) => {
      // If we're collapsing an employee, select their parent
      if (expandedEmployees.has(employeeId)) {
        const parentId = findParentEmployee(employeeId);
        if (parentId) {
          setSelectedEmployee(parentId);
        }
      } else {
        // If expanding, select this employee
        setSelectedEmployee(employeeId);
      }

      // Toggle in the set
      setExpandedEmployees((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(employeeId)) {
          newSet.delete(employeeId);
        } else {
          newSet.add(employeeId);
        }
        return newSet;
      });
    },
    [expandedEmployees, findParentEmployee]
  );

  // Function to expand the org chart to show a specific employee
  const expandToEmployee = useCallback(
    (employeeId: string, specificPath?: OrgChartPath) => {
      const paths = findAllPaths(employeeId);

      if (paths.length === 0) return;

      const pathToUse = specificPath || paths[0].path;
      setSelectedEmployee(employeeId);
      setExpandedPath(pathToUse);

      // Expand all employees in the path
      setExpandedEmployees((prev) => {
        const newSet = new Set(prev);
        pathToUse.forEach((id) => newSet.add(id));
        return newSet;
      });
    },
    [findAllPaths]
  );

  // Derive rootEmployee using useMemo based on selectedEmployee and expandedPath
  const getRootEmployee = useCallback(() => {
    // If we have an expandedPath, use its first element
    if (expandedPath.length > 0) {
      const rootEmployeeId = expandedPath[0];
      return employeeList.find((emp) => emp.id === rootEmployeeId) || null;
    }

    if (selectedEmployee) {
      const rootEmployees = employeeList.filter((emp) => emp.reportsToId === null);

      const isRoot = rootEmployees.some((root) => root.id === selectedEmployee);
      if (isRoot) {
        return employeeList.find((emp) => emp.id === selectedEmployee) || null;
      }

      return rootEmployees.find((root) => root.reports && root.reports.length > 0) || null;
    }

    return employeeList.find((emp) => emp.reportsToId === null) || null;
  }, [selectedEmployee, expandedPath, employeeList]);

  // Get the root employee as a memoized value
  const rootEmployee = useMemo(() => getRootEmployee(), [getRootEmployee]);

  const scrollToEmployee = useCallback((employeeId: string) => {
    // Give the DOM time to update after state changes
    setTimeout(() => {
      const employeeElement = document.getElementById(`employee-${employeeId}`);
      if (employeeElement) {
        employeeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }, 100);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Parse the URL search params
    const searchParams = new URLSearchParams(window.location.search);
    const selectedEmployeeId = searchParams.get(SELECTED_EMPLOYEE_QUERY_PARAM);

    // If there's a selected employee in the URL, expand to that employee
    if (selectedEmployeeId) {
      expandToEmployee(selectedEmployeeId);
      scrollToEmployee(selectedEmployeeId);
    }
  }, [expandToEmployee, scrollToEmployee]);

  // Update URL when selected employee changes
  useEffect(() => {
    // Only run on client-side and when we have a selected employee
    if (typeof window === 'undefined' || selectedEmployee === null) return;

    // Get current URL and update the query parameter
    const url = new URL(window.location.href);
    url.searchParams.set(SELECTED_EMPLOYEE_QUERY_PARAM, selectedEmployee);

    // Update URL without triggering a page reload
    window.history.replaceState({}, '', url.toString());
    expandToEmployee(selectedEmployee);
  }, [expandToEmployee, selectedEmployee]);

  return (
    <OrgChartContext.Provider
      value={{
        employees: employeeList,
        selectedEmployee,
        setSelectedEmployee,
        expandedPath,
        setExpandedPath,
        findAllPaths,
        expandToEmployee,
        rootEmployee,
        scrollToEmployee,
        expandedEmployees,
        toggleEmployeeExpansion,
        isEmployeeExpanded,
        selectEmployee,
      }}
    >
      {children}
    </OrgChartContext.Provider>
  );
};

export { OrgChartProvider };

const useOrgChart = () => {
  const context = useContext(OrgChartContext);
  if (context === undefined) {
    throw new Error('useOrgChart must be used within an OrgChartProvider');
  }
  return context;
};

export default useOrgChart;
